import React, { useEffect, useState } from 'react'
import useSWR from 'swr'
import fetcher from '@lib/fetcher'

const AdminStockItemsEdit = ({ stockItemId }) => {
  const { data: stockItem, mutate } = useSWR(
    `/merchandise/${stockItemId}`,
    fetcher
  )
  const [selectedFile, setSelectedFile] = useState(null)
  const [saving, setSaving] = useState(false)

  const handleFileChange = (e) => {
    setSelectedFile(e.target.files[0])
  }

  const handleSave = async () => {
    if (!selectedFile) return

    setSaving(true)
    const formData = new FormData()
    formData.append('stock_item[standard_image]', selectedFile)

    try {
      const response = await fetch(`/merchandise/${stockItem.slug}`, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]').content,
        },
        body: formData,
      })

      if (response.ok) {
        const updated = await response.json()
        mutate(updated)
        setSelectedFile(null)
        // Reset file input
        document.querySelector('input[type="file"]').value = ''
      } else {
        const error = await response.json()
        alert(`Error: ${error.errors?.join(', ') || 'Failed to update'}`)
      }
    } catch (error) {
      alert(`Error: ${error.message}`)
    } finally {
      setSaving(false)
    }
  }

  if (!stockItem) {
    return <div className="text-gray-600 dark:text-gray-400">Loading...</div>
  }

  return (
    <div className="space-y-6">
      {/* Stock Item Info */}
      <div className="bg-blue-50 dark:bg-blue-900/50 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
        <h3 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">{stockItem.name}</h3>
        <p className="text-sm text-blue-800 dark:text-blue-300">
          All details except images are managed in Spektrix. Only image uploads can be changed here.
        </p>
      </div>

      {/* Stock Item Details */}
      <div className="grid grid-cols-2 gap-4 text-sm">
        <div>
          <span className="font-medium text-gray-700 dark:text-gray-300">Price:</span>
          <span className="ml-2 text-gray-900 dark:text-white">
            £{(stockItem.price || 0).toFixed(2)}
          </span>
        </div>
        <div>
          <span className="font-medium text-gray-700 dark:text-gray-300">Stock Level:</span>
          <span className="ml-2 text-gray-900 dark:text-white">{stockItem.stock_level || 0}</span>
        </div>
        {stockItem.postage_and_packing > 0 && (
          <div>
            <span className="font-medium text-gray-700 dark:text-gray-300">P&P:</span>
            <span className="ml-2 text-gray-900 dark:text-white">
              £{(stockItem.postage_and_packing || 0).toFixed(2)}
            </span>
          </div>
        )}
      </div>

      {/* Description */}
      {stockItem.description && (
        <div>
          <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-2">Description</h4>
          <p className="text-sm text-gray-600 dark:text-gray-400">{stockItem.description}</p>
        </div>
      )}

      {/* Image Upload */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
        <h4 className="font-medium text-gray-700 dark:text-gray-300 mb-4">Image</h4>

        {/* Current Image */}
        {stockItem.image_url && (
          <div className="mb-4">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">Current Image:</p>
            <img
              src={stockItem.image_url}
              alt={stockItem.name}
              className="w-48 h-48 object-cover rounded-lg"
            />
          </div>
        )}

        {/* Upload New Image */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Upload New Image
            </label>
            <input
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              className="block w-full text-sm text-gray-900 dark:text-gray-300
                         file:mr-4 file:py-2 file:px-4
                         file:rounded-md file:border-0
                         file:text-sm file:font-semibold
                         file:bg-blue-50 file:text-blue-700
                         hover:file:bg-blue-100
                         dark:file:bg-blue-900 dark:file:text-blue-200"
            />
          </div>

          {selectedFile && (
            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                disabled={saving}
                className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-medium
                           hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {saving ? 'Saving...' : 'Save Image'}
              </button>
              <button
                onClick={() => {
                  setSelectedFile(null)
                  document.querySelector('input[type="file"]').value = ''
                }}
                className="bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium
                           hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300"
              >
                Cancel
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AdminStockItemsEdit
